import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Wifi, Monitor, Wrench, LifeBuoy } from 'lucide-react';
import { ServiceModal } from '../components/ServiceModal';
import useMetaTags from '../hooks/useMetaTags';
import useStructuredData from '../hooks/useStructuredData';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { CTASectionHTAG } from '../components/CTASectionHTAG';
import { ContactFormModal } from '../components/ContactFormModal';
import { FooterHTAG } from '../components/FooterHTAG';

export const ParticuliersLandingPage = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2, 0.6], [1, 1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.6], [1, 0.96]);
  const heroY = useTransform(scrollYProgress, [0, 0.6], [0, 40]);

  useMetaTags({
    title: 'HTAG Facility | Assistance informatique pour particuliers',
    description: "Assistance informatique pour particuliers a Chateau-Porcien, Rethel et Ardennes. Aide a la configuration, installation d'ordinateur, assistance fibre et depannage. Specialise Apple.",
    keywords: 'assistance informatique particuliers, installation ordinateur, aide configuration, assistance fibre, depannage, Apple, Chateau-Porcien, Rethel, Ardennes, depannage informatique, aide numerique',
    url: 'https://htagfacility.fr/particuliers',
    image: 'https://htagfacility.fr/og-image.png',
    type: 'website'
  });

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'HTAG Facility',
    'description': "Assistance informatique pour particuliers a Chateau-Porcien, Rethel et Ardennes. Installation d'ordinateur, assistance fibre, depannage et aide numerique.",
    'url': 'https://htagfacility.fr/particuliers',
    'image': 'https://htagfacility.fr/og-image.png',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Chateau-Porcien',
      'addressRegion': 'Ardennes',
      'postalCode': '08320',
      'addressCountry': 'FR'
    },
    'areaServed': ['Chateau-Porcien', 'Rethel', 'Ardennes', 'Grand Est'],
    'serviceType': [
      'Installation ordinateur',
      'Configuration informatique',
      'Assistance fibre',
      'Depannage informatique',
      'Aide numerique'
    ],
    'serviceArea': {
      '@type': 'AdministrativeArea',
      'name': 'Ardennes'
    }
  });

  const services = [
    {
      icon: Monitor,
      title: 'Installation et configuration',
      description: 'Mise en route de votre ordinateur, transfert de données, comptes, sauvegardes et mise à jour. Spécialisé Apple et Mac.',
      fullDescription: "Nous prenons en charge l'installation complète de votre ordinateur, la configuration initiale, le transfert de vos données et la création de vos comptes. Nous assurons également la mise à jour et la sauvegarde pour garantir la sécurité de vos informations.",
      benefits: [
        "Installation rapide et personnalisée",
        "Transfert sécurisé de vos données",
        "Configuration de vos comptes et sauvegardes",
        "Conseils pour une utilisation optimale"
      ],
      useCases: [
        "Nouvel ordinateur",
        "Changement de machine",
        "Besoin d'aide pour démarrer",
        "Sécuriser ses données"
      ],
      bgColor: "bg-blue-500"
    },
    {
      icon: Wifi,
      title: 'Assistance fibre et opérateur',
      description: "Aide à l'installation de votre box, configuration Wi-Fi, dépannage fibre et optimisation de votre réseau domestique.",
      fullDescription: "Nous vous accompagnons pour l'installation de votre box internet, la configuration du Wi-Fi et l'optimisation de votre réseau domestique. Nous intervenons aussi en cas de problème de connexion ou de fibre, et pouvons gérer la prise de rendez-vous ainsi que la coordination avec le technicien pour l'installation.",
      benefits: [
        "Installation de la box",
        "Configuration du Wi-Fi",
        "Dépannage fibre",
        "Optimisation du réseau",
        "Coordination avec le technicien"
      ],
      useCases: [
        "Nouvelle connexion",
        "Problème de Wi-Fi",
        "Déménagement",
        "Fibre installée récemment"
      ],
      bgColor: "bg-orange-500"
    },
    {
      icon: Wrench,
      title: 'Dépannage et optimisation',
      description: 'Problèmes, lenteurs, erreurs, nettoyage et optimisation pour un ordinateur plus stable et plus rapide.',
      fullDescription: "Nous résolvons les problèmes de lenteur, erreurs, nettoyage et optimisons votre ordinateur pour qu'il soit plus stable et performant. Intervention sur Apple et Windows.",
      benefits: [
        "Diagnostic complet",
        "Nettoyage et optimisation",
        "Résolution des erreurs",
        "Conseils pour éviter les problèmes"
      ],
      useCases: [
        "Ordinateur lent",
        "Erreurs fréquentes",
        "Besoin d'un nettoyage",
        "Optimisation de la performance"
      ],
      bgColor: "bg-green-500"
    },
    {
      icon: LifeBuoy,
      title: 'Aide numérique au quotidien',
      description: 'Accompagnement pour vos espaces clients, messagerie, téléphonie, sécurité et bonnes pratiques.',
      fullDescription: "Nous vous aidons à utiliser vos outils numériques au quotidien : messagerie, espaces clients, téléphonie, sécurité et bonnes pratiques. Un accompagnement humain et personnalisé.",
      benefits: [
        "Accompagnement personnalisé",
        "Aide sur les outils numériques",
        "Sécurité et bonnes pratiques",
        "Support pour vos démarches en ligne"
      ],
      useCases: [
        "Démarches administratives",
        "Messagerie",
        "Téléphonie",
        "Sécurité numérique"
      ],
      bgColor: "bg-purple-500"
    }
  ];

  const faqs = [
    {
      question: "Vous intervenez sur Apple et Windows ?",
      answer: "Oui. Nous sommes spécialisés Apple, mais nous accompagnons aussi les ordinateurs Windows pour l'installation et le dépannage."
    },
    {
      question: "Est-ce possible à distance ?",
      answer: "Oui. Nous proposons une assistance à distance pour la plupart des demandes. Si besoin, nous pouvons intervenir sur site."
    },
    {
      question: "Vous aidez pour l'installation fibre ?",
        answer: "Oui. Nous pouvons vous accompagner pour l'installation de votre box, la configuration du Wi-Fi et le dépannage de la connexion."
    }
  ];

  return (
    <div className="bg-[#f7f7f8] text-[#111827] overflow-hidden lg:ml-16">
      <NavbarHTAG />
      <ContactFormModal isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />

      <main>
        <section id="accueil" ref={heroRef} className="apple-section min-h-screen flex items-center justify-center px-6 lg:px-8 relative">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 10%, rgba(251, 146, 60, 0.6) 0%, rgba(251, 146, 60, 0.28) 40%, rgba(251, 146, 60, 0.12) 70%, transparent 85%)'
            }}
          />
          <div className="container mx-auto pt-8 relative z-10">
            {/* Toggle Professionnels / Particuliers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="mb-12 flex justify-center gap-2"
            >
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 hover:bg-black/5"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(234, 88, 12, 0.3)',
                  color: '#9a3412'
                }}
              >
                Professionnels
              </button>
              <button
                className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #fb923c 0%, #f97316 60%, #ea580c 100%)',
                  color: '#ffffff',
                  boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)'
                }}
              >
                Particuliers
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ opacity: heroOpacity, scale: heroScale, y: heroY }}
              className="text-center mb-16"
            >
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="apple-title text-5xl lg:text-6xl mt-6"
                style={{
                  color: '#f8fafc',
                  background: 'none',
                  WebkitTextFillColor: '#f8fafc',
                  WebkitBackgroundClip: 'initial',
                  backgroundClip: 'initial'
                }}
              >
                Assistance informatique
                <br />
                simple, claire et humaine
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="apple-subtitle max-w-3xl mx-auto mt-6"
                style={{ color: '#475569' }}
              >
                Aide à la configuration, installation d'ordinateur, assistance fibre,
                dépannage et accompagnement numérique. Spécialisé Apple.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
              >
                <button
                  className="apple-button px-8 py-4 text-lg"
                  style={{
                    background: 'linear-gradient(135deg, #fb923c 0%, #f97316 60%, #ea580c 100%)',
                    boxShadow: '0 10px 30px rgba(234, 88, 12, 0.25)'
                  }}
                  onClick={() => setIsContactFormOpen(true)}
                >
                  Demander de l'aide
                </button>
                <button
                  className="apple-button px-8 py-4 text-lg"
                  style={{
                    background: 'transparent',
                    border: '2px solid rgba(234, 88, 12, 0.35)',
                    color: '#9a3412'
                  }}
                  onClick={() => {
                    const element = document.getElementById('services');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Voir les services
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section id="services" className="apple-section px-6 lg:px-8 relative overflow-hidden">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0f172a', letterSpacing: '-0.03em' }}>
                Services pour particuliers
              </h2>
              <p className="apple-subtitle max-w-2xl mx-auto" style={{ color: '#475569' }}>
                Des solutions simples pour vous dépanner et vous accompagner au quotidien.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="apple-card p-8 rounded-3xl border border-slate-200 cursor-pointer hover:shadow-lg transition-all"
                    style={{ background: '#ffffff' }}
                    onClick={() => setSelectedService(service)}
                  >
                    <div className="w-14 h-14 rounded-2xl apple-gradient-blue p-3 mb-6 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-2xl font-semibold mb-3" style={{ color: '#0f172a' }}>
                      {service.title}
                    </h3>
                    <p className="leading-relaxed" style={{ color: '#475569' }}>
                      {service.description}
                    </p>
                  </motion.div>
                );
              })}
              {/* Modale pour le service sélectionné */}
              <ServiceModal isOpen={!!selectedService} onClose={() => setSelectedService(null)} service={selectedService} />
            </div>
          </div>
        </section>

        <section id="faq" className="py-24 px-4 lg:px-8 bg-gradient-to-b from-[#f7f7f8] via-[#ffffff] to-[#f7f7f8]">
          <div className="container mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0f172a' }}>
                Questions fréquentes
              </h2>
              <p className="text-lg" style={{ color: '#475569' }}>Tout ce qu'il faut savoir pour les particuliers</p>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="rounded-2xl border border-slate-200"
                  style={{ background: '#ffffff' }}
                >
                  <div className="px-6 py-5">
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#0f172a' }}>
                      {faq.question}
                    </h3>
                    <p className="leading-relaxed" style={{ color: '#475569' }}>
                      {faq.answer}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact">
          <CTASectionHTAG onContactClick={() => setIsContactFormOpen(true)} whiteBg />
        </section>
      </main>

      <FooterHTAG whiteBg />
    </div>
  );
};

export default ParticuliersLandingPage;
